import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { clearErrors, readErrors } from '../utils/inventoryErrors';
import { summarizeQueue } from '../utils/queue/status';
import { useInventoryQueueRegistry } from './useInventoryQueueRegistry';

export function useInventoryPending(inventoryId) {
  const queue = useInventoryQueueRegistry();
  // The registry already scopes the journal to the current user and organization.
  // Without an inventory ID, summarize every inventory in that local journal.
  const inventoryIds = useMemo(
    () => (inventoryId ? [inventoryId]
      : [...new Set((queue?.localBatches || []).map((batch) => batch.inventoryId))]),
    [inventoryId, queue?.localBatches],
  );
  const readScopedErrors = useCallback(() => inventoryIds.flatMap((id) => (
    readErrors(id).map((error) => ({ ...error, inventoryId: id }))
  )), [inventoryIds]);
  const [errors, setErrors] = useState(readScopedErrors);

  useEffect(() => {
    const handler = (event) => {
      if (inventoryIds.includes(event.detail?.inventoryId)) setErrors(readScopedErrors());
    };
    window.addEventListener('inventoryErrorsChanged', handler);
    setErrors(readScopedErrors());
    return () => window.removeEventListener('inventoryErrorsChanged', handler);
  }, [inventoryIds, readScopedErrors]);

  return useMemo(() => {
    const batches = (queue?.localBatches || []).filter((b) => !inventoryId || b.inventoryId === inventoryId);
    const { pendingRecords } = summarizeQueue(batches);
    return {
      pendingRecords,
      errors,
      errorsCount: errors.length + (queue?.queueError ? 1 : 0),
      current: !!queue?.queueReady,
      errorMessage: queue?.queueError,
      retry: queue?.flush,
      clear: () => inventoryIds.forEach((id) => clearErrors(id)),
    };
  }, [queue, inventoryId, inventoryIds, errors]);
}
